import EmbedMode from "discourse/lib/embed-mode";
import { withPluginApi } from "discourse/lib/plugin-api";
import {
  COMPOSER_CLOSED_MESSAGE,
  COMPOSER_OPEN_CLASS,
  COMPOSER_OPENED_MESSAGE,
  isTrustedComposerMessage,
  OPEN_COMPOSER_MESSAGE,
  postComposerMessage,
} from "../lib/mod-market-composer-messages";

export const MOD_MARKET_POST_CREATED_EVENT =
  "discourse-mod-market:post-created";

export function isModMarketDiscussionFrame({
  documentElement = document.documentElement,
  parentWindow = window.parent,
  currentWindow = window,
  currentLocation = window.location,
}) {
  if (documentElement.classList.contains("mod-market-discussion")) {
    return true;
  }

  if (parentWindow === currentWindow) {
    return false;
  }

  try {
    return (
      parentWindow.location.origin === currentLocation.origin &&
      new URLSearchParams(currentLocation.search).get("class_name") ===
        "mod-market-discussion"
    );
  } catch {
    return false;
  }
}

export function revealModMarketComposer({
  appEvents,
  documentElement = document.documentElement,
  parentWindow = window.parent,
  origin = window.location.origin,
  focus = false,
}) {
  documentElement.classList.add(COMPOSER_OPEN_CLASS);

  if (parentWindow !== window) {
    postComposerMessage(parentWindow, COMPOSER_OPENED_MESSAGE, origin);
  }

  if (focus) {
    appEvents.trigger("embed-composer:focus");
  }
}

export function hideModMarketComposer({
  documentElement = document.documentElement,
  parentWindow = window.parent,
  origin = window.location.origin,
}) {
  documentElement.classList.remove(COMPOSER_OPEN_CLASS);

  if (parentWindow !== window) {
    postComposerMessage(parentWindow, COMPOSER_CLOSED_MESSAGE, origin);
  }
}

export function handleOpenComposerMessage(event, options) {
  if (
    !isTrustedComposerMessage(event, {
      type: OPEN_COMPOSER_MESSAGE,
      origin: options.origin,
      source: options.parentWindow,
    })
  ) {
    return false;
  }

  revealModMarketComposer({ ...options, focus: true });
  return true;
}

export function registerNestedComposerActions(api, appEvents) {
  api.modifyClass(
    "controller:nested",
    (Superclass) =>
      class extends Superclass {
        replyToPost(post) {
          appEvents.trigger("embed-composer:reply-to-post", post);
        }

        editPost(post) {
          appEvents.trigger("embed-composer:edit-post", post);
        }
      }
  );
}

export function registerModMarketPostCreation(api, appEvents) {
  // EmbedModeComposer commits replies without using the main composer service.
  api.modifyClass(
    "model:post-stream",
    (Superclass) =>
      class extends Superclass {
        commitPost(post) {
          const result = super.commitPost(post);

          if (isModMarketDiscussionFrame({})) {
            appEvents.trigger(MOD_MARKET_POST_CREATED_EVENT, post);
          }

          return result;
        }
      }
  );
}

export default {
  name: "discourse-mod-market-embed-composer",

  initialize(container) {
    const siteSettings = container.lookup("service:site-settings");
    const documentElement = document.documentElement;
    if (
      !siteSettings.discourse_mod_market_enabled ||
      !EmbedMode.enabled ||
      !isModMarketDiscussionFrame({ documentElement })
    ) {
      return;
    }

    documentElement.classList.add("mod-market-discussion");

    const appEvents = container.lookup("service:app-events");
    withPluginApi((api) => {
      registerNestedComposerActions(api, appEvents);
      registerModMarketPostCreation(api, appEvents);
    });

    const options = {
      appEvents,
      documentElement,
      parentWindow: window.parent,
      origin: window.location.origin,
    };
    const revealFromDiscussionAction = () =>
      revealModMarketComposer(options);
    const hideFromComposerAction = () => hideModMarketComposer(options);

    window.addEventListener("message", (event) =>
      handleOpenComposerMessage(event, options)
    );
    appEvents.on(
      "embed-composer:reply-to-post",
      revealFromDiscussionAction
    );
    appEvents.on("embed-composer:edit-post", revealFromDiscussionAction);
    appEvents.on("embed-composer:collapse", hideFromComposerAction);
    appEvents.on(MOD_MARKET_POST_CREATED_EVENT, hideFromComposerAction);
  },
};
